import { Image } from 'react-bootstrap';
import { IMAGE_PATHS } from 'constants/imagePaths';
import {
	signInWithGooglePopup,
	signInWithFacebookPopup,
	signInWithXPopup,
	signInWithApplePopup,
	handleSocialLoginWithEmailCheck,
} from 'utils/firebase.utils';
import {
	logSocialLogin,
	logSocialSignUp,
	useLinkedInCallbackMutation,
	useSocialLoginMutation,
} from 'services';
import { breakTheName } from 'utils';
import { setPreviewsRole, setUserInfo } from 'store/auth';
import { useEffect } from 'react';
import { LINKED_IN_REDIRECT_URL, LINKED_IN_CLIENT_ID } from 'constants/index';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useTranslate from 'hooks/useTranslate';
import toast from 'react-hot-toast';
import { setIsLoading } from 'store/common';
import { secureStorage } from 'utils/secureStorage';

const SocialLogin: React.FC<any> = ({ type }) => {
	const { mutateAsync: socialLoginMutation } = useSocialLoginMutation();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const code = searchParams.get('code');
	const { mutateAsync: linkedInCallbackMutation } = useLinkedInCallbackMutation();

	const { translate } = useTranslate();
	const fcmDeviceToken = secureStorage.getItem('fcmToken') || '';

	useEffect(() => {
		if (code?.length && code?.length > 0) {
			setIsLoading(true);
			linkedInCallbackMutation({ code }).then((res: any) => {
				if (res?.success) {
					const userData = res?.data || {};
					if (userData?.user?.isProfileCompleted) {
						setPreviewsRole(res?.data?.user?.userType);
						setUserInfo(res?.data);
					} else {
						navigate('/profile-update', {
							state: {
								data: res,
							},
						});
					}
					setIsLoading(false);
				} else {
					toast.error(translate(res?.message));
					navigate('/login');
					setIsLoading(false);
				}
			});
		}
	}, [code]);

	const handleAnalytics = (event: string) => {
		if (type === 'login') {
			logSocialLogin(event);
		} else {
			logSocialSignUp(event);
		}
	};

	const generateState = (length = 16) => {
		const array = new Uint8Array(length);
		window.crypto.getRandomValues(array);
		return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
	};

	const state = generateState(16); // Protect against CSRF

	const handleMissingEmail = (provider: string, userData: any) => {
		// Navigate to a special page where user can manually enter their email
		navigate('/profile-update', {
			state: {
				data: {
					success: true,
					data: {
						user: {
							isProfileCompleted: false,
							userType: 'seeker',
						},
						socialData: {
							provider,
							userData,
							requiresEmail: true,
						},
					},
				},
			},
		});
	};

	const handleGoogleSuccess = (response: any) => {
		// Extract email from multiple possible sources
		let email = '';
		if (response?.user?.email) {
			email = response.user.email;
		} else if (response?._tokenResponse?.email) {
			email = response._tokenResponse.email;
		} else if (response?.user?.providerData && response.user.providerData.length > 0) {
			// Check provider data for Google email
			const googleProvider = response.user.providerData.find(
				(p: any) => p.providerId === 'google.com' && p.email,
			);
			if (googleProvider?.email) {
				email = googleProvider.email;
			}
		}

		const payload = {
			socialType: 'google',
			socialId: response?._tokenResponse?.localId || response?.user?.uid,
			udid: response?._tokenResponse?.localId || response?.user?.uid,
			firstName:
				response?._tokenResponse?.firstName || response?.user?.displayName?.split(' ')[0] || '',
			lastName:
				response?._tokenResponse?.lastName ||
				response?.user?.displayName?.split(' ').slice(1).join(' ') ||
				'',
			email: email || '',
			userType: 'seeker',
			platform: 'web',
			deviceToken: fcmDeviceToken,
			voipDeviceToken: '',
		};
		socialLogin(payload);
	};

	const handleFacebookSuccess = (response: any) => {
		const {
			user: { displayName: name, uid, providerData },
		} = response;
		const [firstName, lastName] = breakTheName(name || 'New User');

		// Extract email from multiple possible sources
		let email = '';
		if (response?.user?.email) {
			email = response.user.email;
		} else if (providerData && providerData.length > 0) {
			// Check provider data for Facebook email
			const facebookProvider = providerData.find(
				(p: any) => p.providerId === 'facebook.com' && p.email,
			);
			if (facebookProvider?.email) {
				email = facebookProvider.email;
			} else if (providerData[0]?.email) {
				// Fallback to first provider's email
				email = providerData[0].email;
			}
		}

		const payload = {
			socialType: 'facebook',
			socialId: uid,
			firstName: firstName || ' ',
			email: email || '',
			udid: uid,
			lastName: lastName || ' ',
			userType: 'seeker',
			platform: 'web',
			deviceToken: fcmDeviceToken,
			voipDeviceToken: '',
		};
		socialLogin(payload);
	};

	const handleTwitterSuccess = (response: any) => {
		const {
			user: { displayName: name, uid, providerData },
		} = response;

		const [firstName, lastName] = breakTheName(name || 'New User');

		// Extract email from multiple possible sources
		let email = '';
		if (response?.user?.email) {
			email = response.user.email;
		} else if (providerData && providerData.length > 0) {
			// Check provider data for Twitter email (note: Twitter often doesn't provide email)
			const twitterProvider = providerData.find(
				(p: any) => p.providerId === 'twitter.com' && p.email,
			);
			if (twitterProvider?.email) {
				email = twitterProvider.email;
			} else if (providerData[0]?.email) {
				// Fallback to first provider's email
				email = providerData[0].email;
			}
		}

		const payload = {
			socialType: 'twitter',
			socialId: uid,
			firstName: firstName || ' ',
			email: email || '',
			udid: uid,
			lastName: lastName || ' ',
			userType: 'seeker',
			platform: 'web',
			deviceToken: fcmDeviceToken,
			voipDeviceToken: '',
		};

		socialLogin(payload);
	};

	const handleAppleSuccess = (response: any) => {
		const {
			user: { displayName: name, uid, providerData },
		} = response;

		// Apple might not always return the name
		const [firstName, lastName] = breakTheName(name || 'New User');

		// Extract email from multiple possible sources including private relay
		let email = '';
		if (response?.user?.email) {
			email = response.user.email;
		} else if (providerData && providerData.length > 0) {
			// Check provider data for Apple private relay email
			const appleProvider = providerData.find((p: any) => p.providerId === 'apple.com' && p.email);
			if (appleProvider?.email) {
				email = appleProvider.email;
			}
		} else if (response?._tokenResponse?.email) {
			email = response._tokenResponse.email;
		} else if (response?._tokenResponse?.privateEmail) {
			email = response._tokenResponse.privateEmail;
		}

		const payload = {
			socialType: 'apple',
			socialId: uid,
			firstName: firstName || ' ',
			email: email || '',
			udid: uid,
			lastName: lastName || ' ',
			userType: 'seeker',
			platform: 'web',
			deviceToken: fcmDeviceToken,
			voipDeviceToken: '',
		};

		socialLogin(payload);
	};

	const loginGoogleUser = async () => {
		handleAnalytics('google');
		await handleSocialLoginWithEmailCheck(
			signInWithGooglePopup,
			'google',
			handleGoogleSuccess,
			handleMissingEmail,
		);
	};

	const socialLogin = (payload: any) => {
		socialLoginMutation(payload).then((result: Record<string, any>) => {
			if (result?.success) {
				const userData = result?.data || {};
				if (userData?.user?.isProfileCompleted) {
					setPreviewsRole(result?.data?.user?.userType);
					setUserInfo(result?.data);
				} else {
					navigate('/profile-update', {
						state: {
							data: result,
						},
					});
				}
			} else {
				toast.error(translate(result?.message));
				navigate('/login');
			}
		});
	};

	const loginFacebookUser = async () => {
		handleAnalytics('facebook');
		await handleSocialLoginWithEmailCheck(
			signInWithFacebookPopup,
			'facebook',
			handleFacebookSuccess,
			handleMissingEmail,
		);
	};

	const loginTwitterUser = async () => {
		handleAnalytics('twitter');
		await handleSocialLoginWithEmailCheck(
			signInWithXPopup,
			'twitter',
			handleTwitterSuccess,
			handleMissingEmail,
		);
	};

	const linkedInLogin = () => {
		handleAnalytics('linkedin');
		const scope = 'openid,profile,w_member_social,email';
		const linkedInAuthUrl = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${LINKED_IN_CLIENT_ID}&redirect_uri=${encodeURIComponent(LINKED_IN_REDIRECT_URL)}&scope=${encodeURIComponent(scope)}&state=${state}`;
		window.location.href = linkedInAuthUrl;
	};

	const loginAppleUser = async () => {
		handleAnalytics('apple');
		await handleSocialLoginWithEmailCheck(
			signInWithApplePopup,
			'apple',
			handleAppleSuccess,
			handleMissingEmail,
		);
	};

	return (
		<div className="social-links d-flex justify-content-between">
			<div
				onClick={loginFacebookUser}
				className="social-links-box rounded-pill"
				style={{ cursor: 'pointer' }}
			>
				<Image src={IMAGE_PATHS.FacebookIcon} alt="FacebookIcon" loading="lazy" />
			</div>
			<div
				onClick={loginGoogleUser}
				className="social-links-box rounded-pill"
				style={{ cursor: 'pointer' }}
			>
				<Image src={IMAGE_PATHS.GoogleIcon} alt="GoogleIcon" loading="lazy" />
			</div>

			<div
				onClick={loginAppleUser}
				className="social-links-box rounded-pill"
				style={{ cursor: 'pointer' }}
			>
				<Image src={IMAGE_PATHS.AppleIcon} alt="AppleIcon" loading="lazy" />
			</div>

			<div
				className="social-links-box rounded-pill"
				onClick={linkedInLogin}
				style={{ cursor: 'pointer' }}
			>
				<Image src={IMAGE_PATHS.LinkedinIcon} alt="LinkedinIcon" loading="lazy" />
			</div>

			<div
				onClick={loginTwitterUser}
				className="social-links-box rounded-pill"
				style={{ cursor: 'pointer' }}
			>
				<Image src={IMAGE_PATHS.TwitterIcon} alt="TwitterIcon" loading="lazy" />
			</div>
		</div>
	);
};

export default SocialLogin;
